import {
	ePetitionAttachmentsUpdateQuery,
	ePetitionDeleteAttachmentQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import type { FileUploadItem, FileUploadProps } from '@gov-nx/core/hooks';
import {
	DefaultUploadParameters,
	FileState,
	hasFileId,
	MB,
} from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { PageCode } from '../definitions/codes';
import { usePetitionAttachmentsQuery } from './hooks/usePetitionAttachmentsQuery';

interface PetitionAttachments {
	options: FileUploadProps;
	invalidate: () => Promise<void>;
	files: FileUploadItem[];
}

export const usePetitionAttachments = (
	petitionId: number
): PetitionAttachments => {
	const { t } = useLocale(PageCode['epetice-nova']);
	const { axiosInstance } = useAxiosInstance();
	const { toastMessageSuccess } = useMessageEvents();
	const petitionAttachments = usePetitionAttachmentsQuery(petitionId);

	const uploadAttachment = usePoMutation({
		mutationFn: ePetitionAttachmentsUpdateQuery,
	});

	const deleteAttachmentMutation = usePoMutation({
		mutationFn: ePetitionDeleteAttachmentQuery,
		onSuccess: async () => {
			await petitionAttachments.invalidate();
			toastMessageSuccess(t('formular.zprava.priloha-smazana'));
		},
	});

	return {
		invalidate: petitionAttachments.invalidate,
		files: petitionAttachments.files,
		options: {
			...DefaultUploadParameters,
			extensions: [
				'.xml',
				'.pdf',
				'.doc',
				'.docx',
				'.odt',
				'.jpg',
				'.jpeg',
				'.png',
			],
			maxSumFileSize: 15 * MB,
			maxFileSize: 15 * MB,
			maxAttachments: 10,
			multiple: true,
			onAllFilesUploaded: async (
				files: FileUploadItem[]
			): Promise<FileUploadItem[]> => {
				await Promise.all(
					files.filter(hasFileId).map(({ fileId }) =>
						uploadAttachment.mutateAsync({
							axiosInstance,
							petitionId,
							files: [fileId],
						})
					)
				);
				return await petitionAttachments.refetch();
			},
			onFileRemoved: (file: FileUploadItem) => {
				// Do not delete file that was not successfully uploaded at first place
				if (file.state !== FileState.success) {
					return;
				}

				// Do not delete file that was uploaded but not saved as petition attachment
				if (file.id.toLowerCase().startsWith('GovFormFile'.toLowerCase())) {
					return;
				}

				deleteAttachmentMutation.mutate({
					axiosInstance,
					petitionId,
					attachmentId: parseInt(file.id, 10),
				});
			},
		},
	};
};
